import React, { useCallback, useState, useEffect } from 'react';
import * as _ from 'lodash';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { FETCH_INVOICE, DOWNLOAD_INVOICE, DOWNLOAD_SINGLE_INVOICE } from 'actions/invoice';
import { roundOfDigit, formatNumber, getCustomerName } from 'components/common/utils';
import { Spinner, Row, Col } from 'react-bootstrap';
import fileDownload from 'js-file-download';
import { useTranslation } from 'react-i18next';
import SearchBox from 'components/general/SearchBox';
import { BsSearch, BsDownload } from 'react-icons/bs';
import { IoSearchOutline } from 'react-icons/io5';
import DateTimePicker from 'components/inputs/DateTimePicker';
import Button from 'components/inputs/Button';
import ReactPagination from 'components/general/ReactPagination';

const FleetSessionInvoices = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [startDate, setStartDate] = useState(moment(new Date()).startOf('month'));
  const [endDate, setEndDate] = useState(moment(new Date()).endOf('day'));
  const [downloading, setDownloading] = useState({});

  const isLoader = useSelector((state) => state.invoice.isLoading);
  const invoiceList = useSelector((state) => state.invoice.invoices);
  const page = useSelector((state) => state.invoice.page);
  const limit = useSelector((state) => state.invoice.limit);
  const totalPages = useSelector((state) => state.invoice.totalPages);
  const total = useSelector((state) => state.invoice.total);

  const fetchInvoices = useCallback(
    (data = {}) => {
      const payload = {
        ...data,
        from: moment(startDate).utc(),
        to: moment(endDate).utc(),
        isFleetInvoice: true,
      };
      if (searchText) {
        payload.search = searchText;
      }
      dispatch({ type: FETCH_INVOICE.REQUEST, payload });
    },
    [searchText, startDate, endDate, dispatch]
  );

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleSearch = () => {
    fetchInvoices({});
  };

  const dateFilter = useCallback(() => {
    fetchInvoices({});
  }, [fetchInvoices]);

  const handlePageClick = useCallback(
    (page) => {
      fetchInvoices({ page: page.selected + 1 });
    },
    [fetchInvoices]
  );

  const downloadExcel = () => {
    const payload = {
      excel: true,
      from: moment(startDate).utc(),
      to: moment(endDate).utc(),
      isFleetInvoice: true,
    };
    if (searchText) {
      payload.search = searchText;
    }
    dispatch({
      type: DOWNLOAD_INVOICE.REQUEST,
      payload,
      cb: (res) => {
        if (res && _.get(res, 'status') === 200) {
          fileDownload(res.data, 'Fleet-Session-Invoices.xlsx');
        }
      },
    });
  };

  const singleInvoiceDownload = (item) => {
    const chargerBookingId = item._id ? item._id : item.id;
    setDownloading((prev) => ({ ...prev, [chargerBookingId]: true }));
    dispatch({
      type: DOWNLOAD_SINGLE_INVOICE.REQUEST,
      payload: chargerBookingId,
      cb: (res) => {
        if (res && _.get(res, 'status') === 200) {
          fileDownload(res.data, 'Invoice.pdf');
        }
        setDownloading((prev) => ({ ...prev, [chargerBookingId]: false }));
      },
    });
  };

  const search = (
    <SearchBox
      preIcon={<BsSearch />}
      value={searchText}
      onChange={(e) => setSearchText(e.target.value)}
      onKeyPress={(e) => {
        if (e.key === 'Enter') handleSearch();
      }}
    />
  );
  const searchIcon = <IoSearchOutline size={27} onClick={() => setShowSearchBar(true)} className="report-search-icon" />;

  return (
    <div>
      <Row className="data-report__row">
        <Col xl={'auto'} md={'auto'} className={`${showSearchBar ? '' : 'data-report-search-box'}`}>
          {showSearchBar ? search : searchIcon}
        </Col>
        <Col xl={'auto'} md={'auto'}>
          <div className="data-report-date_picker">
            <DateTimePicker
              onChangeOfStartDate={(item) => setStartDate(item)}
              onChangeOfEndDate={(item) => setEndDate(item)}
              initialValueOfStartDate={moment().startOf('month')}
              initialValueOfEndDate={moment(new Date()).endOf('day')}
            />
          </div>
        </Col>
        <Col xl={'auto'} md={'auto'}>
          <div className="report-search-box" onClick={dateFilter}>
            <Button className="report-search-button">{t('button.search')}</Button>
          </div>
        </Col>
        <Col xl={'auto'} md={'auto'} className="excel-icon--block" onClick={downloadExcel}>
          <div className="report-excel-icon">
            <BsDownload title="Download" size={28} color={'#3c7cdd'} />
          </div>
        </Col>
      </Row>

      <div className="invoice-list--table">
        <div className="table-responsive">
          <table className="record-list-table">
            <thead>
              <tr>
                <th><span>{t('addBillingInvoice.srNo')}</span></th>
                <th><span>{t('addBillingInvoice.customerName')}</span></th>
                <th><span>{t('addBillingInvoice.customerNumber')}</span></th>
                <th><span>{t('addBillingInvoice.email')}</span></th>
                <th><span>{t('addBillingInvoice.ourGSTNumber')}</span></th>
                <th><span>{t('addBillingInvoice.state')}</span></th>
                <th><span>{t('addBillingInvoice.invoiceNumber')}</span></th>
                <th><span>{t('addBillingInvoice.invoiceDate')}</span></th>
                <th><span>{t('addBillingInvoice.unitConsumed')}</span></th>
                <th><span>{t('addBillingInvoice.serviceCharge')}</span></th>
                <th><span>{t('addBillingInvoice.taxableValue')}</span></th>
                <th><span>{t('addBillingInvoice.cgst')}</span></th>
                <th><span>{t('addBillingInvoice.sgst')}</span></th>
                <th><span>{t('addBillingInvoice.igst')}</span></th>
                <th><span>{t('addBillingInvoice.totalInvoice')}</span></th>
                <th><span>{t('addBillingInvoice.downloadInvoice')}</span></th>
              </tr>
            </thead>
            <tbody>
              {isLoader ? (
                <tr>
                  <td colSpan={16} className="border-0">
                    <div className="loader--block">
                      <Spinner as="span" animation="border" size="sm" role="status" />
                      <span className="ps-2">{t('placeHolder.loading')}</span>
                    </div>
                  </td>
                </tr>
              ) : _.isEmpty(invoiceList) ? (
                <tr>
                  <td colSpan={16} className="border-0">
                    <div className="empty-data-block">{t('addBillingInvoice.noInvoice')}</div>
                  </td>
                </tr>
              ) : (
                _.map(invoiceList, (item, key) => {
                  const gst = item.invoice.gst_type === 'SGST' ? roundOfDigit(item.invoice.gst / 2, 2) : '-';
                  const igst = item.invoice.gst_type === 'IGST' ? parseFloat(roundOfDigit(item.invoice.gst, 2)) : '-';
                  const unitConsumed = isNaN(_.get(item, 'meterstop') - _.get(item, 'meterstart') / 1000)
                    ? '-'
                    : roundOfDigit((_.get(item, 'meterstop') - _.get(item, 'meterstart')) / 1000, 3);
                  const serviceCharge = _.get(item, 'invoice.service_charge') ?? 0;
                  const taxableValue = _.get(item, 'invoice.subtotal') ?? 0;
                  const serialNo = limit * (page - 1) + key;
                  const loading = downloading[item._id || item.id];

                  return (
                    <tr key={`fleet-invoice-${serialNo}`}>
                      <td>{serialNo + 1}</td>
                      <td>{getCustomerName(item, 'Guest User')}</td>
                      <td>+{_.get(item, 'customer_user_booked.country_code', '')}{_.get(item, 'customer_user_booked.phone', 'NA')}</td>
                      <td>{_.get(item, 'customer_user_booked.email', 'NA')}</td>
                      <td>{_.get(item, 'invoice.gst_used', '')}</td>
                      <td>{_.get(item, 'charger.charging_station.state', '')}</td>
                      <td>{_.get(item, 'invoice.invoice_no', '-')}</td>
                      <td>{moment(_.get(item, 'invoice.createdAt', '-')).format('DD/MM/YYYY H:mm:ss')}</td>
                      <td>{formatNumber(unitConsumed)}</td>
                      <td>{typeof serviceCharge === 'number' ? serviceCharge.toFixed(2) : ''}</td>
                      <td>{typeof taxableValue === 'number' ? taxableValue.toFixed(2) : ''}</td>
                      <td>{formatNumber(gst)}</td>
                      <td>{formatNumber(gst)}</td>
                      <td>{formatNumber(igst)}</td>
                      <td>
                        {igst === '-' ? (taxableValue + gst * 2).toFixed(2) : (taxableValue + igst).toFixed(2)}
                      </td>
                      <td>
                        {loading ? (
                          <div className="loader--block">
                            <Spinner as="span" animation="border" size="sm" role="status" />
                          </div>
                        ) : (
                          <Button
                            disabled={item?.invoice?.einvoice_generated === false || unitConsumed === 0}
                            onClick={() => singleInvoiceDownload(item)}
                          >
                            {t('button.downloadInvoice')}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      {!isLoader && !_.isEmpty(invoiceList) && (
        <ReactPagination
          currentPage={page}
          limit={limit}
          total={total}
          handlePageClick={(pageVal) => handlePageClick(pageVal)}
          totalPages={totalPages}
          marginPagesDisplayed={1}
        />
      )}
    </div>
  );
};

export default FleetSessionInvoices;
